import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { DomainStatus } from "@/pages/app/CustomDomain";
import { DomainRolloutCard } from "@/components/admin/DomainRolloutCard";

const ALERTS = { dns: "DNS alert", renew: "Renewal alert", expiry: "Expiry alert" };
function Health({ h }) {
  if (!h) return null;
  const open = Object.entries(h.alerts || {}).filter(([, v]) => v).map(([k]) => ALERTS[k]);
  return (
    <span className="text-xs text-muted-foreground w-full" data-testid="admin-domain-health">
      DNS failed checks {h.dns_fails || 0} · renewal failures {h.renew_fails || 0} · cert expires {h.cert_expires_at ? new Date(h.cert_expires_at).toLocaleDateString() : "—"}
      {open.map((a) => <b key={a} className="ml-2 text-red-600">{a} open</b>)}
    </span>
  );
}

const show = (v) => (v === null ? "Unlimited" : v === undefined ? "—" : String(v));

export default function AdminDomains() {
  const [groups, setGroups] = useState(null);
  const load = () => api.get("/admin/domains").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);
  const act = async (d, action) => {
    let reason = "";
    if (action !== "recheck") {
      if (["suspend", "disconnect"].includes(action) && !window.confirm(`${action} ${d.hostname}?\n\n${d.affected_qr} QR code(s) use this domain. Printed QR codes with this domain will stop working; main-domain links keep working.`)) return;
      reason = window.prompt("Reason (required, audited):") || "";
      if (reason.trim().length < 3) return toast.error("A reason is required");
    }
    try { await api.post(`/admin/domains/${d.id}`, { action, reason, confirm: true }); toast.success(`${action} done`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout title="Custom Domains" subtitle="Customer domains by company: verification, routing, HTTPS, allowance and impact.">
      <DomainRolloutCard onSaved={load} />
      {!groups ? null : groups.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-domains-empty">No custom domains yet.</p> : (
        <div className="space-y-4" data-testid="admin-domains">
          {groups.map((g) => (
            <div key={g.company_id} className="rounded-2xl border border-border bg-card p-5" data-testid={`admin-domain-company-${g.company_id}`}>
              <div className="flex flex-wrap gap-x-4 text-sm mb-2"><b>{g.company_name || g.company_id}</b><span className="capitalize text-muted-foreground">{g.plan}</span>
                <span>Feature: <b>{g.feature_enabled ? "on" : "off"}</b></span><span data-testid={`admin-domain-rollout-${g.company_id}`}>Rollout: <b>{g.rollout_open ? "open" : "closed"}</b></span><span>Limit — plan {show(g.plan_limit)} · override {show(g.override_limit)} · effective <b>{show(g.effective_limit)}</b></span></div>
              {g.domains.map((d) => (
                <div key={d.id} data-testid={`admin-domain-${d.id}`} className="flex flex-wrap items-center gap-3 py-2 border-t border-border text-sm">
                  <b className="min-w-[200px]">{d.hostname}</b><DomainStatus s={d.status} />
                  <span className="text-xs text-muted-foreground">TXT {d.checks?.txt ? "✓" : "✗"} · CNAME {d.checks?.routing ? "✓" : "✗"} · cert {d.cert_status || "—"} · {d.affected_qr} QR · last check {d.last_check_at ? new Date(d.last_check_at).toLocaleString() : "never"}</span>
                  {d.entitlement_lost && <span className="text-xs text-amber-600 font-semibold">Plan no longer includes domains — needs resolution</span>}
                  {d.last_error && <span className="text-xs text-red-600 w-full">{d.last_error}</span>}
                  <Health h={d.health} />
                  {d.status !== "disconnected" && <div className="flex gap-1.5 ml-auto">
                    <button onClick={() => act(d, "recheck")} data-testid={`admin-domain-recheck-${d.id}`} className="text-xs border border-border rounded px-2 py-1">Recheck</button>
                    {d.status === "suspended" ? <button onClick={() => act(d, "unsuspend")} data-testid={`admin-domain-unsuspend-${d.id}`} className="text-xs border border-border rounded px-2 py-1">Unsuspend</button>
                      : <button onClick={() => act(d, "suspend")} data-testid={`admin-domain-suspend-${d.id}`} className="text-xs border border-amber-500/50 text-amber-600 rounded px-2 py-1">Suspend</button>}
                    <button onClick={() => act(d, "disconnect")} data-testid={`admin-domain-disconnect-${d.id}`} className="text-xs border border-red-500/40 text-red-600 rounded px-2 py-1">Disconnect</button></div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
